import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  IoHelpCircleOutline,
  IoCheckmarkCircleOutline,
  IoAttachOutline,
  IoDocumentTextOutline,
  IoVideocamOutline,
  IoClose,
  IoBookOutline,
} from "react-icons/io5";
import Button from "../../component/button/Button";
import { creatorSupportRequest, creatorSupportCategories } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import { faqsForTopic } from "../CreatorFaq/faqData";
import RelatedFaqs from "../CreatorFaq/RelatedFaqs";
import style from "./CreatorSupport.module.css";
import MyRequests from "./MyRequests";

// Limits mirror the backend validator (creatorSupport.validator.js).
const SUBJECT_MAX = 150;
const MESSAGE_MIN = 10;
const MESSAGE_MAX = 5000;
// Mirror the backend's supportAttachments middleware.
const ATTACH_MAX_FILES = 3;
const ATTACH_MAX_MB = 10; // images and PDFs
const ATTACH_VIDEO_MAX_MB = 100;
// mp4, .mov (iPhone/Mac screen recordings), webm (browser screen capture)
const ATTACH_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];
const ATTACH_TYPES = ["image/jpeg", "image/png", "application/pdf", ...ATTACH_VIDEO_TYPES];
const isVideo = (file) => ATTACH_VIDEO_TYPES.includes(file.type);

const fmtSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL;

// The Subject dropdown's options come from the backend
// (GET /creators/support/categories) so "Earnings or payouts" only appears
// where creator monetization exists. The chosen label is sent as the ticket
// subject and its key as the category; "Other" lets the creator type their
// own subject. Hints are UI copy, keyed by category, used as the message
// placeholder so creators know which details help.
const OTHER = "other";
const HINTS = {
  upload: "Which video? How big is the file, and where did the upload stop or what error did you see?",
  playback: "Which video, and how long ago was it uploaded? What do you see when you try to play it?",
  publish: "Which title, and what happens when you try to save or publish it?",
  artwork: "Which title and which image (poster, thumbnail, banner)? Any error message?",
  series: "Which series and season? What were you trying to do?",
  account: "What were you trying to change, and what happened?",
  earnings: "Which month or payout is this about, and what looks wrong?",
};
// Used only if the categories request fails — no earnings, since we can't
// tell whether monetization exists.
const FALLBACK_CATEGORIES = [
  { key: "upload", label: "Video upload stuck or failed" },
  { key: "playback", label: "Video not playing or still processing" },
  { key: "publish", label: "Can't publish or edit content" },
  { key: "artwork", label: "Thumbnail or artwork not saving" },
  { key: "series", label: "Series, season or episode problem" },
  { key: "account", label: "Account, login or profile problem" },
  { key: OTHER, label: "Other" },
];
const DEFAULT_HINT =
  "Describe the issue: what you were doing, what you expected, and what happened instead.";

const readAccountEmail = () => {
  try {
    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "null");
    return userInfo?.user?.email || "";
  } catch {
    return "";
  }
};

/**
 * Creator "Help & Support" form. Sends subject + message to the backend,
 * which emails the support inbox with the creator as replyTo — so the answer
 * lands in the creator's own inbox.
 */
const CreatorSupport = () => {
  const location = useLocation();
  // The sidebar link passes the page the creator was on, so support knows
  // where the problem happened.
  const fromPage = location.state?.from || "";
  const accountEmail = readAccountEmail();

  const [categories, setCategories] = useState(null);
  const [issue, setIssue] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [message, setMessage] = useState("");
  // [{ id, file, preview }] — preview is an object URL for images.
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  // Ticket number of the request just sent (null = show the form).
  const [sentId, setSentId] = useState(null);
  const [tab, setTab] = useState("new"); // "new" | "mine"

  useEffect(() => {
    let alive = true;
    creatorSupportCategories()
      .then((res) => {
        const list = res?.data?.data;
        if (alive) setCategories(Array.isArray(list) && list.length ? list : FALLBACK_CATEGORIES);
      })
      .catch(() => {
        if (alive) setCategories(FALLBACK_CATEGORIES);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Revoke image previews when the page unmounts.
  const filesRef = useRef(files);
  filesRef.current = files;
  useEffect(
    () => () => filesRef.current.forEach((f) => f.preview && URL.revokeObjectURL(f.preview)),
    []
  );

  const addFiles = (incoming) => {
    const list = Array.from(incoming || []);
    if (!list.length) return;
    let error;
    const accepted = [];
    for (const file of list) {
      const maxMb = isVideo(file) ? ATTACH_VIDEO_MAX_MB : ATTACH_MAX_MB;
      if (!ATTACH_TYPES.includes(file.type)) {
        error = `${file.name}: only JPG, PNG, PDF, MP4, MOV or WEBM files`;
      } else if (file.size > maxMb * 1024 * 1024) {
        error = `${file.name} is larger than ${maxMb}MB`;
      } else if (files.length + accepted.length >= ATTACH_MAX_FILES) {
        error = `You can attach up to ${ATTACH_MAX_FILES} files`;
      } else {
        accepted.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
          file,
          preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
        });
      }
    }
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
    setErrors((p) => ({ ...p, attachments: error }));
  };

  const removeFile = (id) => {
    const gone = files.find((f) => f.id === id);
    if (gone?.preview) URL.revokeObjectURL(gone.preview);
    setFiles((prev) => prev.filter((f) => f.id !== id));
    setErrors((p) => ({ ...p, attachments: undefined }));
  };

  const clearFiles = () => {
    files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    setFiles([]);
  };

  // A screenshot pasted anywhere in the form (Cmd/Ctrl+V) is attached.
  const handlePaste = (e) => {
    const pasted = Array.from(e.clipboardData?.files || []);
    if (!pasted.length || sending) return;
    e.preventDefault();
    addFiles(pasted);
  };

  const validate = () => {
    const next = {};
    if (!issue) next.issue = "Please choose what this is about";
    else if (issue === OTHER && !customSubject.trim()) next.subject = "Please add a short subject";
    if (!message.trim()) next.message = "Message is required";
    else if (message.trim().length < MESSAGE_MIN)
      next.message = `Please add a bit more detail (at least ${MESSAGE_MIN} characters)`;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending || !validate()) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append("category", issue);
      form.append("subject", subject);
      form.append("message", message.trim());
      if (fromPage) form.append("page", fromPage);
      files.forEach((f) => form.append("attachments", f.file));
      const res = await creatorSupportRequest(form);
      setSentId(res?.data?.data?.ticket_id || true);
      setIssue("");
      setCustomSubject("");
      setMessage("");
      clearFiles();
      setErrors({});
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (status === 422 && data?.errors) {
        setErrors({
          [issue === OTHER ? "subject" : "issue"]: data.errors.subject?.[0],
          message: data.errors.message?.[0],
          attachments: data.errors.attachments?.[0],
        });
      }
      showToast(
        data?.message || "Couldn't send your message. Please try again.",
        "error"
      );
    } finally {
      setSending(false);
    }
  };

  const selected = (categories || []).find((c) => c.key === issue);
  const subject = issue === OTHER ? customSubject.trim() : selected?.label || "";

  return (
    <div className={style.page}>
      <div className={style.header}>
        <h1 className={style.title}>Help &amp; Support</h1>
        <p className={style.subtitle}>
          Something not working? Tell us what happened and our team will reply
          {accountEmail ? (
            <>
              {" "}to <strong>{accountEmail}</strong>.
            </>
          ) : (
            " to your account email."
          )}
        </p>
      </div>

      <div className={style.tabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "new"}
          className={`${style.tab} ${tab === "new" ? style.tabActive : ""}`}
          onClick={() => setTab("new")}
        >
          New request
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "mine"}
          className={`${style.tab} ${tab === "mine" ? style.tabActive : ""}`}
          onClick={() => setTab("mine")}
        >
          My requests
        </button>
      </div>

      {tab === "new" && !sentId && (
        <div className={style.faqNote}>
          <IoBookOutline className={style.faqNoteIcon} />
          <p>
            <strong>Before raising a ticket,</strong> check our{" "}
            <Link to="/mainlayout/creator-faq">FAQ</Link>. Most common problems
            already have a quick answer there.
          </p>
        </div>
      )}

      <div className={style.card}>
        {tab === "mine" ? (
          <MyRequests
            onNewRequest={() => {
              setSentId(null);
              setTab("new");
            }}
          />
        ) : sentId ? (
          <div className={style.success}>
            <IoCheckmarkCircleOutline className={style.successIcon} />
            <h2 className={style.successTitle}>Message sent</h2>
            <p className={style.successText}>
              {typeof sentId === "number" ? `Ticket #${sentId}. ` : ""}
              Thanks for reaching out. We'll get back to you by email.
            </p>
            <div className={style.successActions}>
              <Button onClick={() => setTab("mine")}>View my requests</Button>
              <Button variant="outline" onClick={() => setSentId(null)}>
                Send another message
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} onPaste={handlePaste} noValidate>
            <div className={style.field}>
              <label htmlFor="support-issue" className={style.label}>
                What's this about?
              </label>
              <select
                id="support-issue"
                className={`${style.input} ${style.select} ${
                  errors.issue ? style.inputError : ""
                } ${issue ? "" : style.selectPlaceholder}`}
                value={issue}
                onChange={(e) => {
                  setIssue(e.target.value);
                  if (errors.issue || errors.subject)
                    setErrors((p) => ({ ...p, issue: undefined, subject: undefined }));
                }}
                disabled={sending || !categories}
              >
                <option value="" disabled>
                  {categories ? "Choose a common problem" : "Loading…"}
                </option>
                {(categories || []).map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
              {errors.issue && (
                <div className={style.meta}>
                  <span className={style.error}>{errors.issue}</span>
                </div>
              )}
            </div>

            <RelatedFaqs key={issue} items={faqsForTopic(issue)} />

            {issue === OTHER && (
              <div className={style.field}>
                <label htmlFor="support-subject" className={style.label}>
                  Subject
                </label>
                <input
                  id="support-subject"
                  type="text"
                  className={`${style.input} ${errors.subject ? style.inputError : ""}`}
                  placeholder="e.g. Can't find my uploaded trailer"
                  value={customSubject}
                  maxLength={SUBJECT_MAX}
                  autoFocus
                  onChange={(e) => {
                    setCustomSubject(e.target.value);
                    if (errors.subject) setErrors((p) => ({ ...p, subject: undefined }));
                  }}
                  disabled={sending}
                />
                <div className={style.meta}>
                  <span className={style.error}>{errors.subject}</span>
                  <span className={style.counter}>
                    {customSubject.length}/{SUBJECT_MAX}
                  </span>
                </div>
              </div>
            )}

            <div className={style.field}>
              <label htmlFor="support-message" className={style.label}>
                Message
              </label>
              <textarea
                id="support-message"
                className={`${style.input} ${style.textarea} ${
                  errors.message ? style.inputError : ""
                }`}
                placeholder={HINTS[issue] || DEFAULT_HINT}
                value={message}
                maxLength={MESSAGE_MAX}
                rows={8}
                onChange={(e) => {
                  setMessage(e.target.value);
                  if (errors.message) setErrors((p) => ({ ...p, message: undefined }));
                }}
                disabled={sending}
              />
              <div className={style.meta}>
                <span className={style.error}>{errors.message}</span>
                <span className={style.counter}>
                  {message.length}/{MESSAGE_MAX}
                </span>
              </div>
            </div>

            <div className={style.field}>
              <span className={style.label}>
                Attachments <span className={style.optional}>(optional)</span>
              </span>
              {files.length < ATTACH_MAX_FILES && (
                <button
                  type="button"
                  className={`${style.dropzone} ${dragging ? style.dropzoneActive : ""} ${
                    errors.attachments ? style.inputError : ""
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (!dragging) setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    if (!sending) addFiles(e.dataTransfer.files);
                  }}
                  disabled={sending}
                >
                  <IoAttachOutline className={style.dropzoneIcon} />
                  <span className={style.dropzoneText}>
                    <strong>Add screenshots, files or a screen recording</strong>
                    <span>
                      Drag &amp; drop, click to browse, or paste a screenshot. JPG, PNG or PDF up to {ATTACH_MAX_MB}MB,
                      MP4, MOV or WEBM video up to {ATTACH_VIDEO_MAX_MB}MB. Up to {ATTACH_MAX_FILES} files.
                    </span>
                  </span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ATTACH_TYPES.join(",")}
                multiple
                hidden
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              {files.length > 0 && (
                <ul className={style.fileList}>
                  {files.map((f) => (
                    <li key={f.id} className={style.fileItem}>
                      {f.preview ? (
                        <img src={f.preview} alt="" className={style.fileThumb} />
                      ) : (
                        <span className={style.fileThumb}>
                          {isVideo(f.file) ? <IoVideocamOutline /> : <IoDocumentTextOutline />}
                        </span>
                      )}
                      <span className={style.fileInfo}>
                        <span className={style.fileName}>{f.file.name}</span>
                        <span className={style.fileSize}>{fmtSize(f.file.size)}</span>
                      </span>
                      <button
                        type="button"
                        className={style.fileRemove}
                        onClick={() => removeFile(f.id)}
                        disabled={sending}
                        aria-label={`Remove ${f.file.name}`}
                      >
                        <IoClose />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className={style.meta}>
                <span className={style.error}>{errors.attachments}</span>
                {files.length > 0 && (
                  <span className={style.counter}>
                    {files.length}/{ATTACH_MAX_FILES}
                  </span>
                )}
              </div>
            </div>

            <div className={style.actions}>
              <Button type="submit" loading={sending} disabled={sending}>
                {sending ? (files.some((f) => isVideo(f.file)) ? "Uploading video..." : "Sending...") : "Submit"}
              </Button>
            </div>
          </form>
        )}
      </div>

      {SUPPORT_EMAIL && (
        <p className={style.direct}>
          <IoHelpCircleOutline />
          <span>
            You can also email us directly at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </span>
        </p>
      )}
    </div>
  );
};

export default CreatorSupport;
